import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCustomers({ cfg }) {
  const [rows, setRows] = useState(null);
  const [sweeping, setSweeping] = useState(null);

  const load = () => {
    axios
      .get(`${API}/admin/customers`, cfg())
      .then((r) => setRows(r.data))
      .catch(() => toast.error("Couldn't summon the customer list."));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sweep = async (r) => {
    try {
      const res = await axios.delete(`${API}/admin/customers/${encodeURIComponent(r.email)}`, cfg());
      const n = res.data.orders_removed;
      toast.success(`${r.email} swept — account${n ? ` and ${n} order${n === 1 ? "" : "s"}` : ""} buried.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't sweep that soul.");
    } finally {
      setSweeping(null);
    }
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/customers.csv`, { ...cfg(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "atrocity-customers.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV export failed.");
    }
  };

  return (
    <div data-testid="admin-customers-panel">
      <div className="flex items-center justify-between mb-6">
        <p className="font-type text-sm text-paper/60">
          Every buyer and every registered account, ranked by blood given.
        </p>
        <button
          data-testid="customers-csv-button"
          onClick={downloadCsv}
          className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-4 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>
      {!rows ? (
        <p className="font-type text-sm text-paper/50 py-10 text-center">Summoning...</p>
      ) : rows.length === 0 ? (
        <p className="font-type text-sm text-paper/50 py-10 text-center">No souls yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.email}
              data-testid={`customer-row-${r.email}`}
              className="border border-blooddeep/40 bg-pulp px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1"
            >
              <div className="min-w-[200px]">
                <p className="font-impact uppercase tracking-wide text-sm text-paper">
                  {r.name}
                  {r.role === "admin" && (
                    <span className="ml-2 font-marker text-[10px] text-yell border border-yell/40 px-1.5 py-0.5 align-middle">ADMIN</span>
                  )}
                </p>
                <p className="font-courier text-[11px] text-paper/50">{r.email}</p>
              </div>
              <p className="font-courier text-[11px] uppercase tracking-widest text-paper/50">
                {r.orders} order{r.orders === 1 ? "" : "s"}
              </p>
              <p className="font-marker text-yell text-sm">${r.spent.toFixed(2)}</p>
              {!r.registered && (
                <span className="font-courier text-[10px] uppercase tracking-widest text-paper/35 border border-paper/20 px-2 py-0.5">guest</span>
              )}
              {r.role !== "admin" && (
                <button
                  data-testid={`customer-sweep-${r.email}`}
                  onClick={() => {
                    if (sweeping === r.email) {
                      sweep(r);
                    } else {
                      setSweeping(r.email);
                      setTimeout(() => setSweeping((cur) => (cur === r.email ? null : cur)), 4000);
                    }
                  }}
                  className={`font-impact uppercase tracking-wider text-[11px] border px-3 py-1.5 transition-colors duration-150 ${
                    sweeping === r.email
                      ? "bg-blooddeep text-white border-black"
                      : "text-bloodglow border-blooddeep/50 hover:border-crimson hover:text-crimson"
                  }`}
                >
                  {sweeping === r.email ? "Sure? Wipes orders too" : "Sweep"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
